import { createSelector } from '@reduxjs/toolkit';
import type { LDFlagSet } from 'launchdarkly-react-client-sdk';
import type { OutputSelectorFields } from 'reselect';

import { isTeamingInProgressSelector } from 'features/teaming/teaming-base/store';
import { selectIsViConnectedToFreeSwitchConference } from 'features/caller-session/store';
import {
  isPrimaryHearingConnectedSelector,
  isWavelloCallActiveSelector,
} from 'features/call/call-hearing/store';
import {
  isDeafConnectedSelector,
  selectIsDeafUserFromSorenson,
} from 'features/call/call-deaf/store';
import { isVriModeSelector } from 'features/multi-mode/store';
import {
  callLanguageCodeSelector,
  selectIsConferenceCallType,
} from 'features/call/call-base/store/selectors';
import { AppConfigService } from 'features/app-config/services';
import type { RootState } from 'features/app/store/store';
import { VrsCallType } from 'features/call/vrs-call/enums';
import { normalizeLanguage } from 'features/training-forker/utils';
// Gives the selectIsForkingAllowed selector a more helpful type
// by including the optional `flags` parameter.
type SelectIsForkingAllowed = ((
  state: RootState,
  flags?: LDFlagSet
) => boolean) &
  OutputSelectorFields;

// Determines if training forking is allowed based on multiple conditions.
// These conditions include Redux state and (optionally) LaunchDarkly flags.
export const selectIsForkingAllowed: SelectIsForkingAllowed = createSelector(
  isTeamingInProgressSelector,
  selectIsViConnectedToFreeSwitchConference,
  isWavelloCallActiveSelector,
  isDeafConnectedSelector,
  isPrimaryHearingConnectedSelector,
  isVriModeSelector,
  selectIsConferenceCallType,
  selectIsDeafUserFromSorenson,
  callLanguageCodeSelector,
  // Keep this local to avoid a barrel import cycle that can make the imported selector undefined at module init.
  ({ vrsCall }: RootState) => vrsCall.callType === VrsCallType.EMERGENCY_CALL,
  (_: RootState, flags?: LDFlagSet) => flags,
  (
    isTeamingInProgress,
    isViInFreeSwitchRoom,
    isWavelloCallActive,
    isDeafConnected,
    isPrimaryHearingConnected,
    isVriMode,
    isConferenceCallType,
    isDeafUserFromSorenson,
    callLanguageCode,
    isEmergencyCall,
    flags
  ) => {
    if (flags && !flags.enableTrainingForking) {
      return false;
    }

    if (flags && !flags.allowForkingDuring911 && isEmergencyCall) {
      return false;
    }

    const currentLanguage = normalizeLanguage(callLanguageCode);
    const allowedLanguages = AppConfigService.trainingForkerActiveLanguages;
    const isLanguageAllowed = allowedLanguages.includes(currentLanguage);

    return (
      !isTeamingInProgress &&
      !isViInFreeSwitchRoom &&
      !isWavelloCallActive &&
      isDeafConnected &&
      isPrimaryHearingConnected &&
      !isVriMode &&
      !isConferenceCallType &&
      isDeafUserFromSorenson &&
      isLanguageAllowed
    );
  }
);

export const selectIsQualityDegraded = ({
  trainingForker,
}: RootState): boolean => trainingForker.qualityDegraded;
